import { DayType, MonthSubMode } from '@griply/types';
import {
  getMonthDayNumbers,
  getMonthDayOrdinals,
  getNextSelectedMonthDays,
  getProviderRecurrenceLimits,
  getWeekDays,
  MONTHS,
  parseMonthDaysFromRrule,
  parseMonthFromRrule,
  parseYearWeekDayFromRrule,
  ProviderRecurrenceLimits,
  setYearMonthDaysInRrule,
  setYearSpecificMonthDayInRrule,
} from '@griply/utils';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSelect } from 'shared/components/ui/form-select';
import { RadioInput } from 'shared/components/ui/radio-input';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './form-body.style';
import { MonthDayGrid } from './month-day-grid';

export type MonthsFormProps = {
  rrule: string;
  onChange: (rrule: string) => void;
  weekStartsOn: WeekDays;
  // absent = no target provider (a habit or task schedule) — every option stays offered
  limits?: ProviderRecurrenceLimits;
};

export const RruleYearsBody: React.FC<MonthsFormProps> = ({
  rrule,
  weekStartsOn,
  onChange,
  limits = getProviderRecurrenceLimits(),
}) => {
  const { t } = useTranslation();
  const selectedWeekDay = useMemo(
    () => parseYearWeekDayFromRrule(rrule),
    [rrule],
  );
  const selectedMonthDays = useMemo(
    () => parseMonthDaysFromRrule(rrule),
    [rrule],
  );
  const selectedMonth = useMemo(() => parseMonthFromRrule(rrule), [rrule]);
  const initialOrdinalWeekDay = useMemo(
    () => getWeekDays(weekStartsOn)[0],
    [weekStartsOn],
  );

  const month = selectedMonth ?? 1;

  const onChangeOption = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (target.value === MonthSubMode.SpecificDay) {
      return onChange(
        setYearSpecificMonthDayInRrule(rrule, {
          weekDay: initialOrdinalWeekDay,
          ordinal: limits.monthOrdinals[0],
          month,
        }),
      );
    }

    onChange(setYearMonthDaysInRrule(rrule, [], month));
  };

  const handleToggleMonthDay = (day: number) => {
    onChange(
      setYearMonthDaysInRrule(
        rrule,
        getNextSelectedMonthDays(selectedMonthDays, day, limits),
        month,
      ),
    );
  };

  const onChangeGridMonth = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(
      setYearMonthDaysInRrule(
        rrule,
        selectedMonthDays,
        parseInt(e.target.value),
      ),
    );
  };

  const onChangeOrdinalDay = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!selectedWeekDay) {
      return;
    }

    onChange(
      setYearSpecificMonthDayInRrule(rrule, {
        weekDay: e.target.value as DayType | WeekDays,
        ordinal: selectedWeekDay.ordinal,
        month,
      }),
    );
  };

  const onChangeOrdinalNumber = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!selectedWeekDay) {
      return;
    }

    onChange(
      setYearSpecificMonthDayInRrule(rrule, {
        weekDay: selectedWeekDay.weekDay,
        ordinal: parseInt(e.target.value),
        month,
      }),
    );
  };

  const onChangeOrdinalMonth = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!selectedWeekDay) {
      return;
    }

    onChange(
      setYearSpecificMonthDayInRrule(rrule, {
        weekDay: selectedWeekDay.weekDay,
        ordinal: selectedWeekDay.ordinal,
        month: parseInt(e.target.value),
      }),
    );
  };

  const ordinalOptions = useMemo(
    () =>
      limits.monthOrdinals.map((ordinal) => ({
        label: t(
          `general.labels.habit.repeat.value.ordinal.specific-day.${ordinal}.label`,
        ),
        value: ordinal,
      })),
    [limits.monthOrdinals, t],
  );

  const ordinalDayOptions = useMemo(
    () =>
      getMonthDayOrdinals(weekStartsOn).map((weekDay) => ({
        label: t(
          `general.labels.habit.repeat.specific-days.days.${weekDay}.label`,
        ),
        value: weekDay,
      })),
    [t, weekStartsOn],
  );

  const monthOptions = useMemo(
    () =>
      MONTHS.map((monthValue) => ({
        label: t(`general.labels.habit.repeat.year.months.${monthValue}.label`),
        value: monthValue,
      })),
    [t],
  );

  return (
    <Styled.Container>
      {/* specific day-of-month */}
      <Styled.RadioContainer>
        <Styled.TimesPerContainer>
          <RadioInput
            id={MonthSubMode.SpecificDays}
            name="year-options"
            checked={!selectedWeekDay}
            onChange={onChangeOption}
            value={MonthSubMode.SpecificDays}
          >
            {t('forms.habit.fields.repeat.years.specific-days.label')}
          </RadioInput>

          <FormSelect
            value={selectedMonth}
            options={monthOptions}
            onChange={onChangeGridMonth}
            disabled={!!selectedWeekDay}
            isSmall
          />

          <Typography variant="body2">
            {t('forms.habit.fields.repeat.years.specific-days.on')}
          </Typography>
        </Styled.TimesPerContainer>

        <Styled.RadioBody $disabled={!!selectedWeekDay}>
          <MonthDayGrid
            days={getMonthDayNumbers(month)}
            selectedDays={selectedMonthDays}
            onToggleDay={handleToggleMonthDay}
            disabled={!!selectedWeekDay}
          />
        </Styled.RadioBody>
      </Styled.RadioContainer>

      {/* ordinal weekday */}
      <Styled.RadioContainer>
        <RadioInput
          id={MonthSubMode.SpecificDay}
          name="year-options"
          checked={!!selectedWeekDay}
          onChange={onChangeOption}
          value={MonthSubMode.SpecificDay}
        >
          {t('forms.habit.fields.repeat.months.specific-day.label')}
        </RadioInput>

        <Styled.RadioBody $disabled={!selectedWeekDay}>
          <Styled.TimesPerContainer>
            <FormSelect
              value={selectedWeekDay?.ordinal}
              options={ordinalOptions}
              onChange={onChangeOrdinalNumber}
              disabled={!selectedWeekDay}
              isSmall
            />

            <FormSelect
              value={selectedWeekDay?.weekDay}
              options={ordinalDayOptions}
              onChange={onChangeOrdinalDay}
              disabled={!selectedWeekDay}
              isSmall
            />

            <FormSelect
              value={selectedMonth}
              options={monthOptions}
              onChange={onChangeOrdinalMonth}
              disabled={!selectedWeekDay}
              isSmall
            />
          </Styled.TimesPerContainer>
        </Styled.RadioBody>
      </Styled.RadioContainer>
    </Styled.Container>
  );
};
